/**
 * Utility functions for camera tree visualization
 */

/**
 * Update visible nodes based on current viewport
 * @param {Object} component - Component instance
 */
export const updateVisibleNodes = (component) => {
  const { camera, nodePositions } = component.state;
  const container = component.containerRef.current;

  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const visibleNodes = new Set();

  // Calculate viewport bounds in world coordinates
  const viewportLeft = (-camera.x) / camera.scale;
  const viewportTop = (-camera.y) / camera.scale;
  const viewportRight = viewportLeft + containerRect.width / camera.scale;
  const viewportBottom = viewportTop + containerRect.height / camera.scale;

  // Mobile-specific optimizations (works in both portrait and landscape)
  const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 768;
  const isLandscape = typeof window !== 'undefined' && window.innerHeight < window.innerWidth;
  const isMobileLandscape = isMobile && isLandscape;
  
  // Padding: landscape 150 (more context), mobile portrait 100 (strict), desktop 200
  const basePadding = isMobileLandscape ? 150 : (isMobile ? 100 : 200);
  const padding = Math.max(basePadding, 300 / camera.scale);

  // Node dimensions (TreeNode card size)
  const nodeWidth = 250;
  const nodeHeight = 350;

  // Improved culling: check if node bounds intersect viewport
  nodePositions.forEach((pos, nodeId) => {
    const nodeLeft = pos.x - nodeWidth / 2;
    const nodeRight = pos.x + nodeWidth / 2;
    const nodeTop = pos.y - nodeHeight / 2;
    const nodeBottom = pos.y + nodeHeight / 2;

    // Check if node bounds intersect with viewport bounds (with padding)
    if (nodeRight + padding >= viewportLeft &&
        nodeLeft - padding <= viewportRight &&
        nodeBottom + padding >= viewportTop &&
        nodeTop - padding <= viewportBottom) {
      visibleNodes.add(nodeId);
    }
  });

  component.setState({ visibleNodes });
};

/**
 * Find the middle node of the first row
 * @param {Array} allNodes - All tree nodes
 * @returns {string|null} Node ID or null
 */
export const findMiddleNodeOfFirstRow = (allNodes) => {
  if (!allNodes || allNodes.length === 0) return null;

  const rootNodes = allNodes.filter(node =>
    !node.parentId ||
    node.level === 0 ||
    (node.level === 1 && !allNodes.some(n => n.id === node.parentId))
  );

  if (rootNodes.length === 0) return null;

  const colsPerRow = Math.ceil(Math.sqrt(rootNodes.length));
  const middleCol = Math.floor(colsPerRow / 2);

  if (middleCol < rootNodes.length) {
    return rootNodes[middleCol].id;
  }

  return rootNodes[0].id;
};

/**
 * Get parent path for a node
 * @param {Object} node - Node to get path for
 * @param {Array} allNodes - All nodes in the tree
 * @returns {Array} Array of parent nodes
 */
export const getParentPath = (node, allNodes) => {
  const path = [];
  let currentNode = node;

  while (currentNode.parentId) {
    // eslint-disable-next-line no-loop-func
    const parent = allNodes.find(n => n.id === currentNode.parentId);
    if (parent) {
      path.unshift(parent);
      currentNode = parent;
    } else {
      break;
    }
  }

  return path;
};

/**
 * Normalize text for search - removes accents/diacritics and converts to lowercase
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
const normalizeForSearch = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD') // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/['']/g, "'") // Normalize apostrophes
    .replace(/[""]/g, '"'); // Normalize quotes
};

/**
 * Check if a field contains the search query (accent-insensitive)
 * @param {string} field - Field value to search in
 * @param {string} normalizedQuery - Normalized search query
 * @returns {boolean} Whether the field contains the query
 */
const fieldContainsQuery = (field, normalizedQuery) => {
  if (!field) return false;
  return normalizeForSearch(field).includes(normalizedQuery);
};

// Queries are user text and titles contain metacharacters — "Gastroesophageal
// reflux (GERD)" would blow up an unescaped RegExp
const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Relevance score of a node for a query. Higher wins, 0 means no match.
 *
 * Title hits have to outrank body hits: matches used to be returned in tree
 * order and capped, so a node whose title *is* the query ("Nose strap") could
 * be pushed past the cap by nodes that merely mention the word in their prose.
 *
 * @param {Object} node - Node to score
 * @param {string} normalizedQuery - Normalized search query
 * @returns {number} Relevance score
 */
const scoreNode = (node, normalizedQuery) => {
  const title = normalizeForSearch(node.title);
  if (title) {
    if (title === normalizedQuery) return 100;
    if (title.startsWith(normalizedQuery)) return 80;
    // Query opens a word inside the title — "strap" in "Nose strap"
    if (new RegExp(`\\b${escapeRegExp(normalizedQuery)}`).test(title)) return 70;
    if (title.includes(normalizedQuery)) return 60;
  }
  if (fieldContainsQuery(node.shortDescription, normalizedQuery)) return 40;
  if (fieldContainsQuery(node.description, normalizedQuery)) return 30;
  if (fieldContainsQuery(node.specialistInfo, normalizedQuery)) return 20;
  if (fieldContainsQuery(node.trialDuration, normalizedQuery)) return 15;
  if (fieldContainsQuery(node.links, normalizedQuery)) return 10;
  return 0;
};

/**
 * Handle search query change
 * @param {Object} component - Component instance
 * @param {string} query - Search query
 */
export const handleSearchChange = (component, query) => {
  const { allNodesUnfiltered } = component.props;
  // Use allNodesUnfiltered to search through ALL nodes, not just visible ones
  const searchNodes = allNodesUnfiltered || component.props.allNodes;

  component.setState({ searchQuery: query });

  if (query.trim() === '') {
    component.setState({ searchResults: [] });
    return;
  }

  const normalizedQuery = normalizeForSearch(query);

  const results = searchNodes
    .map(node => ({ node, score: scoreNode(node, normalizedQuery) }))
    .filter(entry => entry.score > 0)
    // Equal relevance: the shorter title is the more specific answer
    .sort((a, b) => b.score - a.score
      || (a.node.title || '').length - (b.node.title || '').length)
    .slice(0, 10)
    // Resolved after the cap — no point walking the tree for discarded matches
    .map(({ node }) => ({
      ...node,
      parentPath: getParentPath(node, searchNodes)
    }));

  component.setState({ searchResults: results });
};

/**
 * Select a search result
 * @param {Object} component - Component instance
 * @param {Object} node - Node to select
 */
export const selectSearchResult = (component, node) => {
  const { onToggleExpansion, expandedNodes } = component.props;

  // Expand all parent nodes to make the selected node visible
  if (node.parentPath && node.parentPath.length > 0 && onToggleExpansion) {
    // Add all parents to expanded nodes
    node.parentPath.forEach(parent => {
      // Only expand if not already expanded
      if (!expandedNodes || !expandedNodes.has(parent.id)) {
        onToggleExpansion(parent.id);
      }
    });
  }

  // Small delay to allow expansion to complete before focusing
  setTimeout(() => {
    // Navigate to the node
    if (component.props.onNodeClick) {
      component.props.onNodeClick(node);
    }
  }, 100);

  // Close search
  component.setState({
    showSearch: false,
    searchQuery: '',
    searchResults: []
  });
};

/**
 * Toggle search visibility
 * @param {Object} component - Component instance
 */
export const toggleSearch = (component) => {
  component.setState(prevState => ({
    showSearch: !prevState.showSearch,
    searchQuery: '',
    searchResults: []
  }));
};

/**
 * Toggle navigation menu
 * @param {Object} component - Component instance
 */
export const toggleNavigation = (component) => {
  component.setState(prevState => ({
    navigationCollapsed: !prevState.navigationCollapsed
  }));
};

/**
 * Toggle minimap
 * @param {Object} component - Component instance
 */
export const toggleMinimap = (component) => {
  component.setState(prevState => ({
    minimapCollapsed: !prevState.minimapCollapsed
  }));
};

/**
 * Toggle camera controls
 * @param {Object} component - Component instance
 */
export const toggleControls = (component) => {
  component.setState(prevState => ({
    controlsCollapsed: !prevState.controlsCollapsed
  }));
};

/**
 * Get current camera state
 * @param {Object} component - Component instance
 * @returns {Object} Camera state
 */
export const getCameraState = (component) => {
  return { ...component.state.camera };
};

/**
 * Set camera state
 * @param {Object} component - Component instance
 * @param {Object} cameraState - Camera state to set
 */
export const setCameraState = (component, cameraState) => {
  if (cameraState) {
    component.setState({ camera: cameraState });
  }
};

/**
 * Reset to first node with animation
 * @param {Object} component - Component instance
 */
export const resetToFirstNode = (component) => {
  setTimeout(() => {
    // Import needed to avoid circular dependency
    const { resetCamera, focusOnNode } = require('./cameraAnimations');

    resetCamera(component);
    setTimeout(() => {
      const { allNodes } = component.props;
      const rootNode = allNodes?.find(node => node.level === 0);

      if (rootNode) {
        focusOnNode(component, rootNode.id);
      } else {
        const middleNodeId = findMiddleNodeOfFirstRow(allNodes);
        if (middleNodeId) {
          focusOnNode(component, middleNodeId);
        }
      }
    }, 500);
  }, 100);
};
